import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  deleteOAuthClient,
  getOAuthClientAllowedScopes,
  listOAuthClients,
  putOAuthClientAllowedScopes,
  registerOAuthClient,
  setOAuthClientActive,
  updateOAuthClient,
} from './api/api'
import { oauthClientAllowedScopesKey, oauthClientsKeys, oauthClientsListKey } from './keyFactory'
import type { OAuthClientInput } from './types'

export function useOAuthClients() {
  return useQuery({ queryKey: oauthClientsListKey(), queryFn: listOAuthClients })
}

export function useRegisterOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: OAuthClientInput) => registerOAuthClient(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientsKeys.all() }),
  })
}

export function useUpdateOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: OAuthClientInput }) => updateOAuthClient(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientsKeys.all() }),
  })
}

export function useDeleteOAuthClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteOAuthClient(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientsKeys.all() }),
  })
}

export function useSetOAuthClientActive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setOAuthClientActive(id, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientsKeys.all() }),
  })
}

export function useOAuthClientAllowedScopes(id: string) {
  return useQuery({
    queryKey: oauthClientAllowedScopesKey(id),
    queryFn: () => getOAuthClientAllowedScopes(id),
    enabled: !!id,
  })
}

export function usePutOAuthClientAllowedScopes(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scopeIds: string[]) => putOAuthClientAllowedScopes(id, scopeIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: oauthClientAllowedScopesKey(id) }),
  })
}
